import React, { useState, useEffect, useRef } from 'react';
import { FiSend, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { messageAPI } from '../services/api';
import { useSocket } from '../context/SocketContext';
import logger from '../utils/logger';

const MatchChat = ({ matchId }) => {
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const messagesEndRef = useRef(null);
  const errorCountRef = useRef(0);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Load initial messages and set up Socket.io listener
  useEffect(() => {
    errorCountRef.current = 0;
    loadMessages();

    // Set up Socket.io listener for real-time messages
    if (socket) {
      socket.on('newMessage', (data) => {
        if (data.matchId === matchId) {
          logger.debug('Real-time message received', { matchId, messageId: data.message._id });
          
          // Only add if it doesn't already exist (prevent duplicates)
          setMessages((prev) => {
            const messageExists = prev.some(m => m._id === data.message._id);
            if (messageExists) {
              logger.debug('Message already exists, skipping duplicate', { messageId: data.message._id });
              return prev;
            }
            return [...prev, data.message];
          });
          scrollToBottom();
        }
      });

      return () => {
        socket.off('newMessage');
      };
    }
  }, [matchId, socket]);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (messages.length > messageCount) {
      scrollToBottom();
      setMessageCount(messages.length);
    }
  }, [messages, messageCount]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const response = await messageAPI.getMessages(matchId);
      if (response.success) {
        setMessages(response.messages);
        setError('');
        errorCountRef.current = 0;
        setMessageCount(response.messages.length);
      }
    } catch (err) {
      errorCountRef.current += 1;
      logger.error('Failed to load messages', { matchId, error: err.message });
      if (errorCountRef.current === 1) {
        setError('Failed to load messages');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await messageAPI.sendMessage(matchId, newMessage);
      if (response.success) {
        // Don't add manually - wait for Socket.io emission or polling
        // This prevents duplicates if Socket.io is slow
        setNewMessage('');
        logger.info('Message sent successfully', { matchId });
        window.dispatchEvent(new Event('refreshNotifications'));
      }
    } catch (err) {
      logger.error('Failed to send message', { matchId, error: err.message });
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await messageAPI.deleteMessage(messageId);
      setMessages(messages.filter(msg => msg._id !== messageId));
      logger.info('Message deleted', { messageId });
    } catch (err) {
      logger.error('Failed to delete message', { messageId, error: err.message });
      setError(err.message || 'Failed to delete message');
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 rounded border border-gray-800">
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {loading ? (
          <p className="text-gray-400 text-center">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="text-gray-500 text-center">No messages yet. Start the conversation!</p>
        ) : (
          messages.map((msg) => (
            <div
              key={msg._id}
              className="flex gap-3 group"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span 
                    onClick={() => navigate(`/profile/${msg.sender._id || msg.sender}`)}
                    className="text-red-500 font-semibold text-sm cursor-pointer hover:underline"
                  >
                    {msg.senderName}
                  </span>
                  <span className="text-gray-600 text-xs">
                    {new Date(msg.createdAt).toLocaleTimeString()}
                  </span>
                </div>
                <p className="text-gray-300 text-sm mt-1">{msg.message}</p>
              </div>
              {/* Delete button only for own messages */}
              {user._id === msg.sender._id && (
                <button
                  onClick={() => handleDeleteMessage(msg._id)}
                  className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-500 transition"
                  title="Delete message"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
            </div>
          ))
        )}
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form
        onSubmit={handleSendMessage}
        className="border-t border-gray-800 p-4"
      >
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 text-white text-sm rounded focus:outline-none focus:border-red-500 transition"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-red-500 hover:bg-red-600 disabled:bg-gray-700 text-white px-4 py-2 rounded transition flex items-center gap-2"
          >
            <FiSend size={16} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default MatchChat;
